import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface EmailInsertEvent {
  id: string;
  user_id: string;
  email_account_id: string;
  message_id: string;
  thread_id: string | null;
  subject: string | null;
  from_address: string;
  from_name: string | null;
  to_addresses: string[];
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  received_at: string;
  created_at: string;
}

type EmailEventHandler = (email: EmailInsertEvent) => void;

let eventHandlers: Set<EmailEventHandler> = new Set();
let activeChannel: any = null;
let subscriberCount = 0;
let knownEmailIds: Set<string> = new Set();
let isInitialLoad = true;

export function useEmailRealtimeManager(
  onEmailInsert?: (email: EmailInsertEvent) => void,
  enabled: boolean = true
) {
  const { user } = useAuth();
  const handlerRef = useRef(onEmailInsert);

  // Keep handler ref updated
  useEffect(() => {
    handlerRef.current = onEmailInsert;
  }, [onEmailInsert]);

  useEffect(() => {
    if (!enabled || !user || !handlerRef.current) return;

    const handler = handlerRef.current;
    eventHandlers.add(handler);
    subscriberCount++;

    // Load existing email IDs on first subscription
    if (subscriberCount === 1) {
      const loadExistingEmails = async () => {
        const { data } = await supabase
          .from('email_messages')
          .select('id')
          .eq('user_id', user.id);
        
        if (data) {
          knownEmailIds = new Set(data.map(e => e.id));
        }
        
        // Mark as loaded after delay
        setTimeout(() => {
          isInitialLoad = false;
        }, 2000);
      };

      loadExistingEmails();
    }

    // Create channel only if it doesn't exist
    if (!activeChannel && subscriberCount > 0) {
      console.log('📡 Creating centralized email subscription');
      
      activeChannel = supabase
        .channel('email-realtime-manager')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'email_messages',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const newEmail = payload.new as EmailInsertEvent;
            
            // Skip if initial load or already known
            if (isInitialLoad || knownEmailIds.has(newEmail.id)) {
              return;
            }

            // Add to known emails
            knownEmailIds.add(newEmail.id);
            
            console.log(`📨 Broadcasting email event to ${eventHandlers.size} handlers`);
            
            // Notify all subscribed handlers
            eventHandlers.forEach(h => {
              try {
                h(newEmail);
              } catch (error) {
                console.error('Error in email event handler:', error);
              }
            });
          }
        )
        .subscribe();
    }

    // Cleanup on unmount
    return () => {
      eventHandlers.delete(handler);
      subscriberCount--;

      // Remove channel when no subscribers left
      if (subscriberCount === 0 && activeChannel) {
        console.log('📡 Removing centralized email subscription (no subscribers)');
        supabase.removeChannel(activeChannel);
        activeChannel = null;
        knownEmailIds.clear();
        isInitialLoad = true;
      }
    };
  }, [enabled, user]);
}
